/**
 * 役職1つの呼び名・受け持ちの範囲・できることを変える画面（issue #124・docs/01 5-4）。
 *
 * 決めごと（決定22）:
 *  - 保存は「保存する」を押したときだけ。押すと、変わるところを確認の窓でお見せしてから保存します
 *  - 「やめる」で役職の一覧へ戻ります（入れたものは保存しません）
 *  - 「初期の内容に戻す」も確認の窓をはさみます
 *  - **ご自身の役職は、呼び名だけ変えられます**（できること・受け持ちの範囲は、ほかの管理者の方に
 *    変えていただく）。**会社全体を管理できる方が1人もいなくなる変更**もサーバが断ります。
 *  - **ご自身が持っていないできることは付けられません**。持っていないできることを含む役職は、
 *    呼び名だけ変えられます（issue #436）
 *  - 「参加しているトークを読む」「…メッセージを送る」は、どの役職でも外せません（issue #441）
 *  - 「会社の設定・拠点の一覧・役職のできることを変える」は、受け持ちの範囲が「全社」の役職にだけ
 *    付けられます（issue #436）
 *    画面の出し分けは見せ方だけで、変えてよいかはサーバの門番が決めます（auth/access.ts）
 *  - 変えた内容は、その場で効きます（門番は毎回、役職の表を読むため）。変えたことは操作履歴に残ります
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import type { RoleRow, RolesResponse } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';
import { can } from '../menu/visible.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';

/** 呼び名の長さの上限（サーバの ROLE_DISPLAY_NAME_MAX_LENGTH と同じ）。 */
const DISPLAY_NAME_MAX_LENGTH = 30;

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
}

interface Draft {
  displayName: string;
  scope: 'org' | 'site';
  capabilities: Record<string, boolean>;
}

type Confirming = { kind: 'none' } | { kind: 'save'; lines: string[] } | { kind: 'reset' };

function scopeLabel(scope: 'org' | 'site'): string {
  return scope === 'org' ? '全社' : '自拠点';
}

export function RoleEditPage(): ReactNode {
  const { key } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { profile } = useSession();
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const [data, setData] = useState<RolesResponse | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState<Confirming>({ kind: 'none' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<RolesResponse>('/org/roles')
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        const role = loaded.roles.find((item) => item.key === key);
        if (role) setDraft(draftOf(role, loaded));
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, key, reloadKey]);

  const role = data?.roles.find((item) => item.key === key) ?? null;
  const ownRole = profile !== null && role !== null && profile.me.role === role.key;
  // 変えられない方には入力欄を出さない（左の項目・一覧の「変える」と同じ出し分け。可否の正はサーバ）
  const canEdit = profile?.grant ? can(profile.grant, 'settings.manage') : false;
  const fixed = new Set(data?.fixed_capabilities ?? []);
  const orgOnly = new Set(data?.org_scope_only_capabilities ?? []);
  /** ご自身が持っているできることか（持っていないものは、ほかの役職にも付けられない・#436） */
  const actorHas = (capability: string): boolean =>
    profile?.grant ? can(profile.grant, capability) : false;
  const actorIsOrgWide = profile?.grant?.scope === 'org';
  /** この役職が持っている、ご自身が持っていないできること（あれば呼び名だけ変えられる・#436） */
  const lacking =
    data && role && !ownRole
      ? data.capabilities.filter(
          (capability) => role.capabilities[capability.key] === true && !actorHas(capability.key),
        )
      : [];
  const outOfScope = role !== null && !ownRole && !actorIsOrgWide && role.scope === 'org';
  const nameOnly = ownRole || lacking.length > 0 || outOfScope;

  function changeLines(): string[] {
    if (!data || !role || !draft) return [];
    const lines: string[] = [];
    if (draft.displayName.trim() !== role.display_name) {
      lines.push(`呼び名：${role.display_name} → ${draft.displayName.trim()}`);
    }
    if (draft.scope !== role.scope) {
      lines.push(`受け持ちの範囲：${scopeLabel(role.scope)} → ${scopeLabel(draft.scope)}`);
    }
    const added: string[] = [];
    const removed: string[] = [];
    for (const capability of data.capabilities) {
      const was = role.capabilities[capability.key] === true;
      const now = draft.capabilities[capability.key] === true;
      if (was === now) continue;
      (now ? added : removed).push(capability.description);
    }
    if (added.length > 0) lines.push(`できるようにする：${added.join('、')}`);
    if (removed.length > 0) lines.push(`できないようにする：${removed.join('、')}`);
    return lines;
  }

  function askSave(): void {
    setResult(null);
    const lines = changeLines();
    if (lines.length === 0) {
      setResult({ tone: 'info', message: '変えたところはありません。' });
      return;
    }
    setConfirming({ kind: 'save', lines });
  }

  async function confirm(): Promise<void> {
    if (!role || !draft || confirming.kind === 'none') return;
    setBusy(true);
    try {
      if (confirming.kind === 'save') {
        await api.patch(`/admin/roles/${role.key}`, {
          body: {
            display_name: draft.displayName.trim(),
            capabilities: draft.capabilities,
            scope: draft.scope,
          },
        });
      } else {
        await api.del(`/admin/roles/${role.key}`);
      }
      setResult({
        tone: 'success',
        message:
          confirming.kind === 'save'
            ? `「${draft.displayName.trim()}」の役職を保存しました。`
            : `「${role.display_name}」の役職を初期の内容に戻しました。`,
        nextStep:
          'できる・できないは、その場で変わります。ほかの方の画面の左の項目は、画面を開き直したときに変わります。',
      });
      setConfirming({ kind: 'none' });
      setReloadKey((n) => n + 1);
    } catch (caught: unknown) {
      setConfirming({ kind: 'none' });
      setResult({
        tone: 'error',
        message: withReason('保存できませんでした', toMessage(caught)),
        ...(caught instanceof ApiError && (caught.status === 403 || caught.status === 409)
          ? {}
          : { nextStep: '内容をご確認のうえ、もう一度お試しください。' }),
      });
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <article>
        <PageHeader title="役職を変える" />
        <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} />
      </article>
    );
  }
  if (profile !== null && !canEdit) {
    return (
      <article>
        <PageHeader title="役職を変える" />
        <ErrorBlock
          message="役職のできることは、いまの役職では変えられません。"
          nextStep={wording.ask(ROLE_REQUIREMENTS.roleSettings)}
        />
        <Link to="/roles">役職とできることの一覧へ戻る</Link>
      </article>
    );
  }
  if (!data) return <LoadingBlock label="役職を読み込んでいます…" />;
  if (!role || !draft) {
    return (
      <article>
        <PageHeader title="役職を変える" />
        <ErrorBlock
          message="この役職は見つかりませんでした。"
          nextStep="役職の一覧に戻って、開き直してください。"
        />
        <Link to="/roles">役職とできることの一覧へ戻る</Link>
      </article>
    );
  }

  return (
    <article>
      <PageHeader
        title={`役職を変える：${role.display_name}`}
        description="呼び名・受け持ちの範囲・できることを変えます。変えた内容は、その場で効きます。"
        actions={
          <Link className="ui-button ui-button-secondary" to="/roles">
            役職とできることの一覧へ戻る
          </Link>
        }
      />
      {ownRole ? (
        <ResultBanner
          tone="info"
          message="ご自身の役職です。呼び名だけ変えられます。"
          nextStep="できること・受け持ちの範囲を変えたいときは、ほかの管理者の方にご依頼ください。"
        />
      ) : lacking.length > 0 ? (
        <ResultBanner
          tone="info"
          message={`あなたの役職に無い「できること」（${lacking.map((capability) => capability.description).join('、')}）を含む役職です。呼び名だけ変えられます。`}
          nextStep="できること・受け持ちの範囲を変えたいときは、そのできることを持つ管理者の方にご依頼ください。"
        />
      ) : outOfScope ? (
        <ResultBanner
          tone="info"
          message="受け持ちの範囲が「全社」の役職です。呼び名だけ変えられます。"
          nextStep={wording.ask(ROLE_REQUIREMENTS.orgRoleSettings, {
            lead: 'できること・受け持ちの範囲を変えたいときは',
          })}
        />
      ) : null}

      <FormCard
        submitLabel="保存する"
        busy={busy && confirming.kind === 'save'}
        onSubmit={askSave}
        onCancel={() => navigate('/roles')}
        footer={
          result ? (
            <ResultBanner
              tone={result.tone}
              message={result.message}
              reveal
              {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
            />
          ) : undefined
        }
      >
        <label className="ui-field" htmlFor="role-display-name">
          <span>呼び名</span>
          <input
            id="role-display-name"
            type="text"
            value={draft.displayName}
            maxLength={DISPLAY_NAME_MAX_LENGTH}
            onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
          />
        </label>
        <label className="ui-field" htmlFor="role-scope">
          <span>受け持ちの範囲</span>
          <select
            id="role-scope"
            value={draft.scope}
            disabled={nameOnly}
            onChange={(event) => setDraft(withScope(draft, event.target.value, orgOnly))}
          >
            {/* ご自身より広い範囲は付けられない（#436） */}
            <option value="org" disabled={!actorIsOrgWide}>
              全社
            </option>
            <option value="site">自拠点</option>
          </select>
        </label>
        <fieldset className="admin-choice-group">
          <legend className="admin-setting-label">できること</legend>
          <p className="admin-note">
            {'「メンバーをCSVでまとめて登録する」は、「メンバーを管理する」と一緒に付けてください（片方だけでは取り込めません）。' +
              'あなたの役職に無い「できること」は、ほかの役職にも付けられません。'}
          </p>
          {data.capabilities.map((capability) => {
            const note = fixed.has(capability.key)
              ? 'どの役職でも外せません'
              : orgOnly.has(capability.key) && draft.scope !== 'org'
                ? '受け持ちの範囲が「全社」の役職にだけ付けられます'
                : !nameOnly && !actorHas(capability.key)
                  ? 'あなたの役職に無いため、付けられません'
                  : null;
            return (
              <label key={capability.key} className="ui-check">
                <input
                  type="checkbox"
                  checked={fixed.has(capability.key) || draft.capabilities[capability.key] === true}
                  disabled={nameOnly || note !== null}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      capabilities: {
                        ...draft.capabilities,
                        [capability.key]: event.target.checked,
                      },
                    })
                  }
                />
                <span>
                  {capability.description}
                  {note !== null ? <small className="admin-check-note">（{note}）</small> : null}
                </span>
              </label>
            );
          })}
        </fieldset>
      </FormCard>

      <div className="admin-row-actions">
        <Button
          variant="secondary"
          busy={busy && confirming.kind === 'reset'}
          onClick={() => {
            setResult(null);
            setConfirming({ kind: 'reset' });
          }}
        >
          初期の内容に戻す
        </Button>
      </div>

      <ConfirmDialog
        open={confirming.kind !== 'none'}
        title={
          confirming.kind === 'reset' ? '役職を初期の内容に戻します' : '役職の内容を保存します'
        }
        tone="primary"
        message={
          confirming.kind === 'save' ? (
            <>
              「{role.display_name}」を次のように変えます。
              {confirming.lines.map((line) => (
                <span key={line}>
                  <br />
                  {line}
                </span>
              ))}
            </>
          ) : (
            `「${role.display_name}」の呼び名・受け持ちの範囲・できることを、はじめに用意した内容に戻します。`
          )
        }
        reversibility="この画面でいつでも変え直せます。変えたことは操作履歴に残ります。"
        reversible
        confirmLabel={confirming.kind === 'reset' ? '初期の内容に戻す' : '保存する'}
        busy={busy}
        onConfirm={() => void confirm()}
        onCancel={() => setConfirming({ kind: 'none' })}
      />
    </article>
  );
}

/** 画面で使う入力の形（できることは一覧の全項目を真偽値でそろえる。サーバも全項目を求める）。 */
function draftOf(role: RoleRow, data: RolesResponse): Draft {
  const fixed = new Set(data.fixed_capabilities ?? []);
  const capabilities: Record<string, boolean> = {};
  for (const capability of data.capabilities) {
    // 外せない項目は、いつも「できる」で送る（サーバは「できない」を断る・#441）
    capabilities[capability.key] =
      fixed.has(capability.key) || role.capabilities[capability.key] === true;
  }
  return { displayName: role.display_name, scope: role.scope, capabilities };
}

/**
 * 受け持ちの範囲を変える。「自拠点」にしたら、全社の役職にだけ付けられる項目は外す（#436）。
 * 外したことは、保存の確認の窓に「できないようにする」として出る。
 */
function withScope(draft: Draft, value: string, orgOnly: ReadonlySet<string>): Draft {
  const scope = value === 'org' ? 'org' : 'site';
  if (scope === 'org') return { ...draft, scope };
  const capabilities = { ...draft.capabilities };
  for (const key of orgOnly) if (capabilities[key] === true) capabilities[key] = false;
  return { ...draft, scope, capabilities };
}

function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
